import type { Router } from 'vue-router';
import { isNativeApp, loadDeviceName } from '@/lib/native';
import { interceptApiLinks } from '@/lib/native-files';
import { setupAppLock } from '@/lib/app-lock';
import { setupShareTarget } from '@/lib/shared-inbox';
import { setupPush } from '@/lib/native-push';

/**
 * Comportements propres à la coque native, chargés seulement dans l'app (le
 * web ne télécharge pas ces modules) :
 *  - lien du site ouvert depuis un email → la page correspondante dans l'app ;
 *  - bouton retour Android → page précédente, puis sortie de l'app ;
 *  - liens vers des fichiers de l'API → téléchargés avec le jeton, puis partagés ;
 *  - photo ou PDF partagé depuis une autre app → page Dépenses (lib/shared-inbox.ts) ;
 *  - notifications push et ouverture de la page touchée (lib/native-push.ts) ;
 *  - verrouillage biométrique (lib/app-lock.ts) ;
 *  - barre d'état accordée au thème ; écran de démarrage masqué une fois prêt.
 */
export async function setupNativeShell(router: Router): Promise<void> {
  if (!isNativeApp) return;
  const [{ App }, { SplashScreen }, { StatusBar, Style }] = await Promise.all([
    import('@capacitor/app'),
    import('@capacitor/splash-screen'),
    import('@capacitor/status-bar'),
    loadDeviceName(),
  ]).then(([app, splash, status]) => [app, splash, status] as const);

  // Alerte native de la WebView : simple et lisible pour un échec rare.
  interceptApiLinks((message) => window.alert(message));
  // Photo ou PDF partagé depuis une autre app → analysé comme justificatif.
  await setupShareTarget(router, (message) => window.alert(message)).catch(() => undefined);

  await App.addListener('appUrlOpen', ({ url }) => {
    try {
      const { pathname, search, hash } = new URL(url);
      void router.push(`${pathname}${search}${hash}`);
    } catch {
      // URL illisible : on reste où l'on est.
    }
  });

  await App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else void App.exitApp();
  });

  const syncStatusBar = () =>
    StatusBar.setStyle({ style: document.documentElement.classList.contains('dark') ? Style.Dark : Style.Light }).catch(
      () => undefined,
    );
  void syncStatusBar();
  new MutationObserver(syncStatusBar).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

  await router.isReady();
  await setupPush(router).catch(() => undefined);
  // Avant de masquer l'écran de démarrage : le contenu n'apparaît jamais déverrouillé.
  await setupAppLock().catch(() => undefined);
  await SplashScreen.hide();
}
